import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import ExpandableRow from '../../../components/ExpandableRow/ExpandableRow';
import { useTranslation } from 'react-i18next';

const AddRoleDialog = ({
  open,
  onClose,
  permissions = [],
  onAddRole,
  initialName = '',
  initialDescription = '',
  initialPermissions = [],
  permissionsEditable = true,
  title,
  saveLabel,
  templateCode = null
}) => {
  
  const [roleName, setRoleName] = useState(initialName);
  const [roleDescription, setRoleDescription] = useState(initialDescription);
  const [selectedPermissions, setSelectedPermissions] = useState(initialPermissions);
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      setRoleName(initialName);
      setRoleDescription(initialDescription);
      setSelectedPermissions(initialPermissions);
    }
  }, [open, initialName, initialDescription, initialPermissions]);

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSave = () => {
    const newRoleData = {
      name: roleName,
      description: roleDescription,
      permissions: selectedPermissions,
      templateCode
    };
    onAddRole(newRoleData);
    setRoleName(''); // Reset role name
    setRoleDescription(''); // Reset role description
    setSelectedPermissions([]); // Reset selected permissions
  };

  const handleClose = () => {
    setRoleName(''); // Reset role name
    setRoleDescription(''); // Reset role description
    setSelectedPermissions([]); // Reset selected permissions
    onClose();
  };

  const getSelectedCountForCategory = (categoryPermissions) => {
    return categoryPermissions.filter((permission) => selectedPermissions.includes(permission.id)).length;
  };

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="add-role-dialog-title">
      <DialogTitle id="add-role-dialog-title">{title || t('add_new_rol')}</DialogTitle>
      <DialogContent className="permission-dialog" sx={{ padding: 0 }}>
        <div style={{paddingLeft: '16px', paddingRight: '16px'}}>
          <TextField
            autoFocus
            margin="dense"
            label={t('rol_name')}
            type="text"
            fullWidth
            variant="outlined"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            className="input_role"
          />
          <TextField
            autoFocus
            margin="dense"
            label={t('rol_description')}
            type="text"
            fullWidth
            variant="outlined"
            value={roleDescription}
            onChange={(e) => setRoleDescription(e.target.value)}
            className="input_role"
          />
        </div>
        <TableContainer 
          component={Paper} 
          sx={{ 
            width: '100%', 
            margin: 0,
            marginLeft: 0,
            marginRight: 0,
            maxWidth: '100%',
            '& .MuiTable-root': {
              width: '100%',
              tableLayout: 'fixed'
            }
          }}
        >
          <Table sx={{ width: '100%', tableLayout: 'fixed', display: 'table', borderCollapse: 'collapse' }} aria-label="permissions table">
            <TableHead>
              <TableRow className="permission-cell-compat" sx={{ width: '100%' }}>
                <TableCell sx={{ width: '70%', padding: '4px 16px' }}><span className="roboto-medium">{t('permission')}</span></TableCell>
                <TableCell sx={{ width: '30%', padding: '4px 16px' }} align="right"><span className="roboto-medium">{t('enabled')}</span></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions.length > 0 ? (
                permissions.map(({ category, permissions }) => (
                  <ExpandableRow
                    key={category}
                    category={category}
                    permissions={permissions}
                    rolePermissions={selectedPermissions}
                    onToggle={handlePermissionToggle}
                    editable={permissionsEditable}
                    selectedCount={getSelectedCountForCategory(permissions)}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">{t('empty_permission')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <Button onClick={handleSave} color="primary" variant="contained">{saveLabel || t('add')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRoleDialog;
